import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../App';
import { 
  Landmark, Wallet, Shield, CreditCard, 
  ArrowRight, TrendingUp, TrendingDown, RefreshCw,
  Building2, Users, IndianRupee
} from 'lucide-react';
import { Button } from '../components/ui/button';

const Accounts = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    family: { count: 0 },
    banks: { count: 0, totalBalance: 0 },
    demat: { count: 0, totalValue: 0 },
    las: { count: 0, totalLoan: 0, utilization: 0 },
    mtf: { count: 0, totalLoan: 0 }
  });

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const [familyRes, bankRes, dematRes, holdingsRes, debtRes, dashboardRes] = await Promise.all([
        api.get('/family-members').catch(() => ({ data: [] })),
        api.get('/bank-accounts').catch(() => ({ data: [] })),
        api.get('/demat-accounts').catch(() => ({ data: [] })),
        api.get('/holdings').catch(() => ({ data: [] })),
        api.get('/debt/summary').catch(() => ({ data: {} })),
        api.get('/dashboard/summary').catch(() => ({ data: {} }))
      ]);

      const family = familyRes.data || [];
      const banks = bankRes.data || [];
      const demat = dematRes.data || [];
      const holdings = holdingsRes.data || [];
      const debt = debtRes.data || {};
      const dashboard = dashboardRes.data || {};

      // Calculate bank totals by account type
      let savingsBalance = 0;
      let odBalance = 0;
      let savingsCount = 0;
      let odCount = 0;
      
      banks.forEach(b => {
        const balance = b.available_balance || b.balance || 0;
        if (b.account_type === 'savings') {
          savingsBalance += balance;
          savingsCount++;
        } else if (b.account_type === 'loan' || b.account_type === 'od' || b.account_type === 'current') {
          odBalance += balance;
          odCount++;
        }
      });
      
      const totalBankBalance = savingsBalance + odBalance;

      // Calculate demat data from holdings (grouped by bank_name which represents demat accounts)
      const dematBanks = {};
      let totalHoldings = 0;
      holdings.forEach(h => {
        const bank = h.bank_name || 'Unassigned';
        if (!dematBanks[bank]) {
          dematBanks[bank] = { count: 0, quantity: 0 };
        }
        dematBanks[bank].count++;
        dematBanks[bank].quantity += h.quantity || 0;
        totalHoldings++;
      });
      const dematAccountCount = Object.keys(dematBanks).filter(k => k !== 'Unassigned').length;
      const dematBankNames = Object.keys(dematBanks).filter(k => k !== 'Unassigned');

      // Get LAS data from debt summary
      const lasSummary = debt.las_summary || {};
      const bajajLoans = debt.bajaj_loans || {};
      const lasCount = lasSummary.total_records || 0;
      const lasPledged = lasSummary.total_pledged || 0;
      const lasEligible = lasSummary.total_eligible || 0;
      const bajajUsed = bajajLoans.total_used || 0;
      const bajajLimit = bajajLoans.total_limit || 0;
      const lasUtilization = bajajLoans.utilization_percent || (lasEligible > 0 ? (bajajUsed / lasEligible * 100) : 0);

      // Get MTF data from debt summary
      const mtfSummary = debt.mtf_summary || {};
      const mtfAccounts = mtfSummary.accounts || [];
      const mtfCount = mtfAccounts.length;
      const mtfOutstanding = mtfSummary.total_outstanding || 0;

      // Count unique LAS accounts from banks
      const lasBanks = Object.keys(lasSummary.by_bank || {});

      setSummary({
        family: { count: family.length, members: family },
        banks: { 
          count: banks.length, 
          totalBalance: totalBankBalance,
          savingsBalance,
          savingsCount,
          odBalance,
          odCount
        },
        demat: { 
          count: dematAccountCount || demat.length, 
          totalHoldings,
          bankNames: dematBankNames,
          portfolioValue: dashboard.total_portfolio_value || 0
        },
        las: { 
          count: lasBanks.length || lasCount, 
          totalLoan: bajajUsed, 
          pledgedValue: lasPledged,
          utilization: lasUtilization 
        },
        mtf: { count: mtfCount, totalLoan: mtfOutstanding }
      });
    } catch (err) {
      console.error('Failed to fetch account summary:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₹0';
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} L`;
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const accountTypes = [
    {
      id: 'family',
      title: 'Family Accounts',
      description: 'Manage family members and their portfolio allocations',
      icon: Users,
      path: '/family-members',
      color: 'bg-indigo-500',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200',
      stats: [
        { label: 'Total Members', value: summary.family.count },
        { label: 'Members', value: summary.family.members?.slice(0, 4).map(m => m.name).join(', ') + (summary.family.count > 4 ? '...' : '') || '-' }
      ]
    },
    {
      id: 'bank',
      title: 'Bank Accounts',
      description: 'View and manage your bank accounts and balances',
      icon: Landmark,
      path: '/banks',
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      stats: [
        { label: 'Savings', value: `${summary.banks.savingsCount || 0} · ${formatCurrency(summary.banks.savingsBalance)}` },
        { label: 'Current (OD)', value: `${summary.banks.odCount || 0} · ${formatCurrency(summary.banks.odBalance)}` }
      ]
    },
    {
      id: 'demat',
      title: 'Demat Accounts',
      description: 'View your demat holdings and portfolio distribution',
      icon: Wallet,
      path: '/demat',
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      stats: [
        { label: 'Brokers', value: summary.demat.bankNames?.join(', ') || '-' },
        { label: 'Total Holdings', value: `${summary.demat.totalHoldings || 0} stocks` }
      ]
    },
    {
      id: 'las',
      title: 'LAS Accounts',
      description: 'Loan Against Securities - manage pledged shares and loans',
      icon: Shield,
      path: '/las',
      color: 'bg-amber-500',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      stats: [
        { label: 'Total Accounts', value: summary.las.count },
        { label: 'Total Loan', value: formatCurrency(summary.las.totalLoan) },
        { label: 'Utilization', value: `${summary.las.utilization.toFixed(1)}%` }
      ]
    },
    {
      id: 'mtf',
      title: 'MTF Accounts',
      description: 'Margin Trading Facility - track margin positions and interest',
      icon: CreditCard,
      path: '/mtf',
      color: 'bg-emerald-500',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      stats: [
        { label: 'Total Accounts', value: summary.mtf.count },
        { label: 'Total Loan', value: formatCurrency(summary.mtf.totalLoan) }
      ]
    }
  ];

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Building2 className="text-primary" size={32} />
              Accounts
            </h1>
            <p className="text-gray-600 mt-1">Manage all your financial accounts in one place</p>
          </div>
          <Button onClick={fetchSummary} className="btn-outline flex items-center gap-2" disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
          </Button>
        </div>

        {/* Quick Overview - Clickable Cards */}
        <div className="card bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <IndianRupee className="text-primary" size={20} />
            Quick Overview
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div 
              onClick={() => navigate('/banks')}
              className="text-center p-4 bg-white rounded-lg cursor-pointer hover:shadow-md hover:bg-blue-50 transition-all"
            >
              <p className="text-sm text-gray-500">Total Bank Balance</p>
              <p className="text-xl font-bold text-blue-600">{formatCurrency(summary.banks.totalBalance)}</p>
            </div>
            <div 
              onClick={() => navigate('/demat')}
              className="text-center p-4 bg-white rounded-lg cursor-pointer hover:shadow-md hover:bg-purple-50 transition-all"
            >
              <p className="text-sm text-gray-500">Demat Holdings</p>
              <p className="text-xl font-bold text-purple-600">{summary.demat.totalHoldings || 0} stocks</p>
              <p className="text-sm text-purple-500">{formatCurrency(summary.demat.portfolioValue)}</p>
            </div>
            <div 
              onClick={() => navigate('/las')}
              className="text-center p-4 bg-white rounded-lg cursor-pointer hover:shadow-md hover:bg-amber-50 transition-all"
            >
              <p className="text-sm text-gray-500">LAS Loan</p>
              <p className="text-xl font-bold text-amber-600">{formatCurrency(summary.las.totalLoan)}</p>
            </div>
            <div 
              onClick={() => navigate('/mtf')}
              className="text-center p-4 bg-white rounded-lg cursor-pointer hover:shadow-md hover:bg-emerald-50 transition-all"
            >
              <p className="text-sm text-gray-500">MTF Loan</p>
              <p className="text-xl font-bold text-emerald-600">{formatCurrency(summary.mtf.totalLoan)}</p>
            </div>
          </div>
        </div>

        {/* Account Type Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {accountTypes.map((account) => {
            const Icon = account.icon;
            return (
              <div
                key={account.id}
                onClick={() => navigate(account.path)}
                className={`card ${account.bgColor} ${account.borderColor} border-2 cursor-pointer hover:shadow-lg transition-all group`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 ${account.color} rounded-xl text-white`}>
                    <Icon size={28} />
                  </div>
                  <ArrowRight 
                    size={24} 
                    className="text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all" 
                  />
                </div>

                <h2 className="text-xl font-bold text-gray-900 mb-1">{account.title}</h2>
                <p className="text-gray-600 text-sm mb-4">{account.description}</p>

                <div className="flex flex-wrap gap-4 pt-4 border-t border-gray-200">
                  {account.stats.map((stat, idx) => (
                    <div key={idx} className="flex-1 min-w-[100px]">
                      <p className="text-xs text-gray-500 uppercase tracking-wider">{stat.label}</p>
                      <p className="text-lg font-bold text-gray-900">{stat.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
};

export default Accounts;
